/**
 * TestCleanupSection — test-account cleanup on the completion panel (Bill 2026-09-15).
 *
 * A test application (last name Test with a 666 SSN/TIN/ATIN or CIF) queues its
 * own cleanup when it commits: the NetTeller user, the debit cards it issued,
 * the accounts and the CIFs it created, in kill order. Everything is held while
 * the officer tests. "Done testing" releases it now; otherwise it starts on its
 * own after test.cleanup.autoReleaseDays (14).
 *
 * NetTeller has to go first and only SysOps can delete it (MNTUSR), so SysOps
 * gets a Teams card and the rest runs automatically when they mark it done.
 * Renders nothing for a real application (no queued test cleanup).
 */
import { useCallback, useEffect, useState } from 'react';
import type { KillListEntryDto } from '@retail-ops/shared-types';
import { ApiError } from '@retail-ops/api-client';
import { Banner, Button, ModalShell, Pill } from '@/components/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useApi } from './hooks/useApi';

const TYPE_LABEL: Record<string, string> = {
  NT: 'NetTeller',
  ATM: 'Debit card',
  DDA: 'Checking',
  SAV: 'Savings',
  CD: 'CD',
  IRA: 'IRA',
  BOX: 'Safe deposit box',
  CIF: 'Customer record',
};

interface EntryMeta {
  testCleanup?: { released?: boolean; releaseAfterUtc?: string };
  execution?: { detail?: string; routedToSysOps?: boolean };
}

function metaOf(e: KillListEntryDto): EntryMeta {
  if (!e.metadataJson) return {};
  try {
    return JSON.parse(e.metadataJson) as EntryMeta;
  } catch {
    return {};
  }
}

function isOpen(e: KillListEntryDto): boolean {
  return e.status !== 'executed' && e.status !== 'cancelled';
}

function stateOf(e: KillListEntryDto): { label: string; variant: 'ok' | 'warn' | 'err' | 'info' | 'muted' } {
  if (e.status === 'executed') return { label: 'Done', variant: 'ok' };
  if (e.status === 'cancelled') return { label: 'Cancelled', variant: 'muted' };
  const meta = metaOf(e);
  if (meta.testCleanup && !meta.testCleanup.released) return { label: 'Held for testing', variant: 'info' };
  if (meta.execution?.routedToSysOps) return { label: 'With SysOps', variant: 'warn' };
  if (e.failedReason) return { label: 'Stopped', variant: 'err' };
  return { label: 'Queued', variant: 'muted' };
}

function formatDay(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

export function TestCleanupSection({ cartId }: { cartId: string }): JSX.Element | null {
  const { client } = useApi();
  const { user } = useAuth();
  const [entries, setEntries] = useState<ReadonlyArray<KillListEntryDto>>([]);
  const [loaded, setLoaded] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ tone: 'ok' | 'info' | 'err'; text: string } | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const resp = await client.retail.killList.list({ cartId, reason: 'TestAccount', top: 50 });
      setEntries(resp.entries ?? []);
    } catch {
      // Fail-soft: the completion panel never blocks on the kill list.
    } finally {
      setLoaded(true);
    }
  }, [client, cartId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!loaded || entries.length === 0) return null;

  const ordered = [...entries].sort((a, b) => (a.killOrder ?? 5) - (b.killOrder ?? 5));
  const held = ordered.filter((e) => isOpen(e) && metaOf(e).testCleanup?.released === false);
  const releaseDay = formatDay(held.map((e) => metaOf(e).testCleanup?.releaseAfterUtc).find(Boolean));
  const allDone = ordered.every((e) => !isOpen(e));

  const runCleanup = async (): Promise<void> => {
    const anchor = ordered.find(isOpen) ?? ordered[0];
    if (!anchor) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await client.retail.killList.releaseTestCleanup(anchor.killListEntryId, user?.upn ?? '');
      setResult({ tone: r.complete ? 'ok' : r.success ? 'info' : 'err', text: r.message });
    } catch (err) {
      setResult({
        tone: 'err',
        text: ApiError.isApiError(err) ? err.message : err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusy(false);
      setConfirming(false);
      void load();
    }
  };

  const intro = allDone
    ? 'Everything this test application created has been killed.'
    : held.length > 0
      ? `This is a test application, so its records are queued for cleanup and held while you test. Click Done testing when you are finished${releaseDay ? `, or cleanup starts on its own on ${releaseDay}` : ''}.`
      : 'Cleanup is running. SysOps deletes NetTeller first; the rest follows automatically when they mark it done.';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <span
        style={{
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          fontWeight: 600,
          color: 'var(--ink-500)',
        }}
      >
        Test account cleanup
      </span>
      <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>{intro}</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
        {ordered.map((e) => {
          const st = stateOf(e);
          const detail = metaOf(e).execution?.detail ?? e.failedReason ?? undefined;
          const showDetail = st.label === 'With SysOps' || st.label === 'Stopped';
          return (
            <div
              key={e.killListEntryId}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 'var(--sp-3)',
                fontSize: 12,
                padding: 'var(--sp-2) var(--sp-3)',
                border: '1px solid var(--surface-border)',
                borderRadius: 'var(--r-md)',
              }}
            >
              <span style={{ minWidth: 120, color: 'var(--ink-700)' }}>
                {TYPE_LABEL[(e.targetType ?? '').toUpperCase()] ?? e.targetType ?? 'Record'}
              </span>
              <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>{e.targetId}</span>
              <Pill variant={st.variant}>{st.label}</Pill>
              {showDetail && detail && (
                <span style={{ flexBasis: '100%', color: 'var(--ink-500)' }}>{detail}</span>
              )}
            </div>
          );
        })}
      </div>

      {result && <Banner tone={result.tone}>{result.text}</Banner>}

      {held.length > 0 && (
        <div>
          <Button variant="primary" onClick={() => setConfirming(true)} disabled={busy}>
            Done testing, clean up
          </Button>
        </div>
      )}

      {confirming && (
        <ModalShell
          open
          size="md"
          title="Clean up this test account?"
          onClose={busy ? () => {} : () => setConfirming(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirming(false)} disabled={busy}>
                Not yet
              </Button>
              <Button variant="primary" onClick={() => void runCleanup()} disabled={busy}>
                {busy ? 'Cleaning up…' : 'Yes, clean it up'}
              </Button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', fontSize: 13 }}>
            <p style={{ margin: 0 }}>
              This kills everything this test application created, in order: the NetTeller user, the debit card,
              the accounts, then the customer records it created. Customers that already existed are left alone.
            </p>
            <p style={{ margin: 0 }}>
              SysOps deletes NetTeller first and gets a Teams card for it. The rest runs automatically when they mark
              it done. Accounts and customer records purge at that night&rsquo;s day-end; the debit card is removed on
              the 1st of the month.
            </p>
            <p style={{ margin: 0, fontWeight: 600 }}>This cannot be undone.</p>
          </div>
        </ModalShell>
      )}
    </div>
  );
}
